import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Box, Button, TextField, Typography, CircularProgress } from '@mui/material'
import { useQueryClient } from '@tanstack/react-query'
import { getCurrentUser } from '@/utils/auth/UserProvider'
import { useUpdateUserMutation } from '@/utils/hooks/users'
import { getPostLoginRedirect } from '@/utils/postLoginRedirect'
import { LogoutButton } from '@/components/AuthButtons'
import { COLORS } from './theme'
import { stepHeadingSx, stepDescriptionSx, inputSx, textFieldSx, primaryButtonSx } from './styles'
import { SEGMENTS } from './onboardingSteps'

/** Static stepper that mirrors OnboardingStepperBanner layout for zero shift on transition. */
function NamePageStepper() {
  return (
    <Box
      sx={{
        display: 'flex',
        width: '100%',
        gap: 0.5,
        px: 3,
        py: 2,
      }}
    >
      {SEGMENTS.map((segment, i) => (
        <Box
          key={segment.label}
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 0.5,
            borderRadius: 1,
            px: 0.5,
            py: 0.5,
          }}
        >
          <Box
            sx={{
              width: '100%',
              height: 8,
              borderRadius: 4,
              backgroundColor: i === 0 ? COLORS.primary : COLORS.border,
              opacity: i === 0 ? 0.7 : 1,
            }}
          />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minHeight: 20 }}>
            <Typography
              variant="caption"
              sx={{
                color: i === 0 ? COLORS.primary : COLORS.lightGray,
                fontWeight: i === 0 ? 600 : 400,
                fontSize: '0.7rem',
                visibility: i === 0 ? 'visible' : 'hidden',
              }}
            >
              {i === 0 ? 'Name' : segment.label}
            </Typography>
          </Box>
        </Box>
      ))}
    </Box>
  )
}

/**
 * Standalone name collection page at /onboarding/name.
 * Gates email/password users who have no first_name/last_name.
 * Google users skip this entirely (Auth0 pre-fills their name).
 */
export default function OnboardingNamePage() {
  const { user, token } = getCurrentUser()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const updateUser = useUpdateUserMutation()

  const [firstName, setFirstName] = useState(user?.first_name ?? '')
  const [lastName, setLastName] = useState(user?.last_name ?? '')
  const [touched, setTouched] = useState({ first: false, last: false })
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const firstMissing = firstName.trim().length === 0
  const lastMissing = lastName.trim().length === 0
  const isValid = !firstMissing && !lastMissing

  async function handleContinue() {
    if (!isValid || !user?.uuid || !token) return

    setSaving(true)
    setError(null)
    try {
      await updateUser.mutateAsync({
        token,
        editedUser: { ...user, first_name: firstName.trim(), last_name: lastName.trim() },
      })
      // Wait for the user data refetch to complete so AuthenticatedRoute
      // sees the updated name before we navigate away
      await queryClient.invalidateQueries({ queryKey: ['currentUser'] })
      // If there's a pending invite redirect, go there so the user can accept.
      // Otherwise default to onboarding (OnboardingShell redirects to / if complete).
      const pendingRedirect = getPostLoginRedirect()
      navigate(pendingRedirect ?? '/onboarding', { replace: true })
    } catch {
      setError('Something went wrong. Please try again.')
      setSaving(false)
    }
  }

  return (
    <Box
      sx={{
        height: '100vh',
        backgroundColor: COLORS.background,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      {/* Top bar — matches OnboardingShell layout for zero shift on transition */}
      <Box
        sx={{
          flexShrink: 0,
          backgroundColor: 'white',
          borderBottom: `1px solid ${COLORS.border}`,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            maxWidth: 960,
            mx: 'auto',
            width: '100%',
          }}
        >
          <Box sx={{ pl: 3, display: 'flex', alignItems: 'center' }}>
            <img
              src="/Fundsorter-h-black800px.png"
              alt="Fundsorter"
              style={{ height: 24, width: 'auto' }}
            />
          </Box>
          <Box sx={{ flex: 1 }}>
            <NamePageStepper />
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, pr: 3 }}>
            {/* Reserve space for save indicator to match shell layout */}
            <Box sx={{ width: 14, height: 14, flexShrink: 0 }} />
            <LogoutButton />
          </Box>
        </Box>
      </Box>

      {/* Centre: content card */}
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          pt: 3,
          pb: 1,
          px: 2,
        }}
      >
        <Box
          sx={{
            width: '100%',
            maxWidth: 960,
            flex: 1,
            minHeight: 0,
            backgroundColor: 'white',
            borderRadius: '16px',
            boxShadow: '0 4px 24px rgba(0,0,0,0.08)',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              overflowY: 'auto',
              p: { xs: 3, sm: 5 },
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              '&::before, &::after': { content: '""', flex: '1 0 0px' },
            }}
          >
            <Box sx={{ width: '100%', maxWidth: 560 }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
                <img
                  src="/fundsorter-straight-arm.webp"
                  alt="Fundsorter"
                  style={{ height: 80, marginBottom: 24 }}
                />

                <Typography variant="h5" sx={{ ...stepHeadingSx, mb: 1 }}>
                  Kia ora! What's your name?
                </Typography>

                <Typography sx={{ ...stepDescriptionSx, textAlign: 'center', mb: 3 }}>
                  Please enter your own name for your user account. You'll enter your
                  organisation's name in a moment. You will be your organisation's admin.
                  You can add more users or assign different admins later.
                </Typography>

                <Box sx={{ width: '100%', maxWidth: 360, display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <TextField
                    autoFocus
                    fullWidth
                    required
                    label="First name"
                    value={firstName}
                    onChange={e => setFirstName(e.target.value)}
                    onBlur={() => setTouched(t => (t.first ? t : { ...t, first: true }))}
                    error={touched.first && firstMissing}
                    helperText={touched.first && firstMissing ? 'Please enter your first name' : undefined}
                    sx={textFieldSx}
                    slotProps={{ input: { sx: inputSx } }}
                  />
                  <TextField
                    fullWidth
                    required
                    label="Last name"
                    value={lastName}
                    onChange={e => setLastName(e.target.value)}
                    onBlur={() => setTouched(t => (t.last ? t : { ...t, last: true }))}
                    onKeyDown={e => { if (e.key === 'Enter' && isValid && !saving) handleContinue() }}
                    error={touched.last && lastMissing}
                    helperText={touched.last && lastMissing ? 'Please enter your last name' : undefined}
                    sx={textFieldSx}
                    slotProps={{ input: { sx: inputSx } }}
                  />
                </Box>

                {error && (
                  <Typography sx={{ color: COLORS.error, mt: 2, fontSize: '0.9rem' }}>
                    {error}
                  </Typography>
                )}
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Bottom: Continue button */}
      <Box
        sx={{
          flexShrink: 0,
          pb: 2,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 1,
        }}
      >
        {!isValid && !saving && (
          <Typography sx={{ color: COLORS.secondary, fontSize: '0.85rem', textAlign: 'center' }}>
            Enter your first and last name to continue.
          </Typography>
        )}
        <Button
          variant="contained"
          disabled={!isValid || saving}
          onClick={handleContinue}
          sx={{
            ...primaryButtonSx,
            px: 6,
            py: 1.5,
            fontSize: '1rem',
          }}
        >
          {saving ? <CircularProgress size={22} sx={{ color: 'white' }} /> : 'Continue'}
        </Button>
      </Box>
    </Box>
  )
}
